import React, { createContext, useContext, useState, useCallback } from "react";
import { toast } from "sonner";

interface InternetContextProps {
  internetEnabled: boolean;
  toggleInternet: () => void;
  enableInternet: () => void;
  disableInternet: () => void;
}

const InternetContext = createContext<InternetContextProps | undefined>(
  undefined
);

export const useInternet = (): InternetContextProps => {
  const context = useContext(InternetContext);
  if (!context) {
    throw new Error("useInternet must be used within an InternetProvider");
  }
  return context;
};

interface InternetProviderProps {
  children: React.ReactNode;
}

export const InternetProvider: React.FC<InternetProviderProps> = ({
  children,
}) => {
  const [internetEnabled, setInternetEnabled] = useState<boolean>(false);

  const toggleInternet = useCallback(() => {
    setInternetEnabled((prev) => {
      const newState = !prev;
      toast.success(`Internet access ${newState ? "enabled" : "disabled"}`);
      return newState;
    });
  }, []);

  const enableInternet = useCallback(() => {
    setInternetEnabled(true);
    toast.success("Internet access enabled");
  }, []);

  const disableInternet = useCallback(() => {
    setInternetEnabled(false);
    toast.success("Internet access disabled");
  }, []);

  return (
    <InternetContext.Provider
      value={{
        internetEnabled,
        toggleInternet,
        enableInternet,
        disableInternet,
      }}
    >
      {children}
    </InternetContext.Provider>
  );
};
